/**
 * RDAEditor Component
 * Edit Responsive Display Ad text assets and images
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { TrashIcon, CloseIcon } from '@/components/ui/Icons';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { getRDA, updateRDA, getLinkedImagesByEntity, type RDAImageAsset, type GetRDAResponse } from '@/services/api/assets';
import { debug, toast } from '@/utils';
import { TargetedImagePicker } from '@/components/media/TargetedImagePicker';
import { CroppedThumbnail } from '@/components/media/CroppedThumbnail';
import { pendingKey, type PendingTargeted } from '@/types/media';
import { useMaterializePendingTargeted } from '@/hooks/useMaterializePendingTargeted';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { RDA_LIMITS } from '@/constants/googleAdsLimits';
import { ImageThumbnailWithActions } from '@/components/common/ImageThumbnailWithActions';

interface RDAEditorProps {
  memberUuid: string;
  campaignId?: string;  // For usage tracking
  adGroupId: string;
  adId: string;
  customerId?: string;
  adGroupName?: string;  // Pre-populated ad group name for immediate display
  initialData?: GetRDAResponse;  // Pre-fetched RDA data to avoid duplicate API call
  onSaved: () => void;
  onCancel: () => void;
}

const HEADLINE_LIMIT = RDA_LIMITS.HEADLINE_CHARS;
const LONG_HEADLINE_LIMIT = RDA_LIMITS.LONG_HEADLINE_CHARS;
const DESCRIPTION_LIMIT = RDA_LIMITS.DESCRIPTION_CHARS;
const BUSINESS_NAME_LIMIT = RDA_LIMITS.BUSINESS_NAME_CHARS;
const MIN_HEADLINES = RDA_LIMITS.MIN_HEADLINES;
const MAX_HEADLINES = RDA_LIMITS.MAX_HEADLINES;
const MIN_DESCRIPTIONS = RDA_LIMITS.MIN_DESCRIPTIONS;
const MAX_DESCRIPTIONS = RDA_LIMITS.MAX_DESCRIPTIONS;

// Image picker modes
type ImagePickerMode = 'marketing' | 'square' | 'logo' | 'landscapeLogo';

// Helper component for image preview with remove button (uses shared ImageThumbnailWithActions)
interface ImagePreviewProps {
  imageUrl?: string;
  assetId: string;
  aspectRatio: string;
  onRemove: () => void;
  label?: string;
}

function ImagePreview({ imageUrl, aspectRatio, onRemove, label }: ImagePreviewProps) {
  // Determine size based on aspect ratio
  const getSize = () => {
    switch (aspectRatio) {
      case '1.91:1': return { width: 76, height: 40 };
      case '1:1': return { width: 64, height: 64 };
      case '4:1': return { width: 80, height: 20 };
      default: return { width: 64, height: 64 };
    }
  };

  const size = getSize();

  return (
    <ImageThumbnailWithActions
      imageUrl={imageUrl}
      alt={label || 'Image'}
      size={size}
      onRemove={onRemove}
      removeLabel="Remove image"
      confirmTitle="Remove Image"
      confirmMessage="Are you sure you want to remove this image from the ad?"
    />
  );
}

// Pending-crop preview — renders a CSS-cropped view of the source image
// without writing anything to GCS. Used for the "newly cropped, not yet
// uploaded" row of thumbnails in the editor. Materialization happens once
// the user clicks Save Changes (handleSave).
interface PendingPreviewProps {
  pending: PendingTargeted;
  aspectRatio: string;
  onRemove: () => void;
}

function PendingPreview({ pending, aspectRatio, onRemove }: PendingPreviewProps) {
  const getSize = () => {
    switch (aspectRatio) {
      case '1.91:1': return { width: 76, height: 40 };
      case '1:1': return { width: 64, height: 64 };
      case '4:1': return { width: 80, height: 20 };
      default: return { width: 64, height: 64 };
    }
  };
  return (
    <ImageThumbnailWithActions
      alt={pending.source.alt_text || pending.source.original_filename}
      size={getSize()}
      onRemove={onRemove}
      removeLabel="Remove image"
      confirmTitle="Remove Image"
      confirmMessage="Are you sure you want to remove this image from the ad?"
    >
      <CroppedThumbnail source={pending.source} cropBox={pending.cropBox} />
    </ImageThumbnailWithActions>
  );
}

export function RDAEditor({
  memberUuid,
  campaignId,
  adGroupId,
  adId,
  customerId,
  adGroupName: initialAdGroupName,
  initialData,
  onSaved,
  onCancel,
}: RDAEditorProps) {
  const materializePending = useMaterializePendingTargeted();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // RDA text data
  const [headlines, setHeadlines] = useState<string[]>([]);
  const [longHeadline, setLongHeadline] = useState<string>('');
  const [descriptions, setDescriptions] = useState<string[]>([]);
  const [businessName, setBusinessName] = useState<string>('');
  const [finalUrls, setFinalUrls] = useState<string[]>([]);
  const [adGroupName, setAdGroupName] = useState<string>(initialAdGroupName || '');

  // RDA image data - original images from Google Ads (by asset_id)
  const [marketingImages, setMarketingImages] = useState<RDAImageAsset[]>([]);
  const [squareMarketingImages, setSquareMarketingImages] = useState<RDAImageAsset[]>([]);
  const [logoImages, setLogoImages] = useState<RDAImageAsset[]>([]);
  const [landscapeLogoImages, setLandscapeLogoImages] = useState<RDAImageAsset[]>([]);

  // New images selected from Media Library — held as `PendingTargeted` (in
  // memory only) until Save Changes. Targeted MediaImages aren't materialized
  // (no GCS write, no DB row) until handleSave runs, so cancelling the editor
  // or re-cropping leaves zero orphaned rows. Same pattern as PMax.
  const [newMarketingPending, setNewMarketingPending] = useState<PendingTargeted[]>([]);
  const [newSquarePending, setNewSquarePending] = useState<PendingTargeted[]>([]);
  const [newLogoPending, setNewLogoPending] = useState<PendingTargeted[]>([]);
  const [newLandscapeLogoPending, setNewLandscapeLogoPending] = useState<PendingTargeted[]>([]);

  // Image picker state
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [imagePickerMode, setImagePickerMode] = useState<ImagePickerMode>('marketing');

  // Load RDA data
  useEffect(() => {
    async function loadRDA() {
      setLoading(true);
      setError(null);

      try {
        // Use initialData if provided, otherwise fetch from API
        const rdaResponse = initialData?.success
          ? initialData
          : await getRDA({
              member_uuid: memberUuid,
              ad_group_id: adGroupId,
              ad_id: adId,
              customer_id: customerId,
            });

        // Always fetch linked images for thumbnails
        const linkedImagesResponse = await getLinkedImagesByEntity({
          member_uuid: memberUuid,
          entity_type: 'ad',
          entity_id: adId,
          customer_id: customerId,
        });

        if (rdaResponse.success) {
          setHeadlines(rdaResponse.headlines || []);
          setLongHeadline(rdaResponse.long_headline || '');
          setDescriptions(rdaResponse.descriptions || []);
          setBusinessName(rdaResponse.business_name || '');
          setFinalUrls(rdaResponse.final_urls || []);
          setAdGroupName(rdaResponse.ad_group_name || initialAdGroupName || '');

          // Build lookup map from google_ads_asset_id to thumbnail_url
          const thumbnailMap = new Map<string, string>();
          if (linkedImagesResponse.success && linkedImagesResponse.images) {
            for (const img of linkedImagesResponse.images) {
              if (img.google_ads_asset_id && img.thumbnail_url) {
                thumbnailMap.set(img.google_ads_asset_id, img.thumbnail_url);
              }
            }
          }
          debug.log('Built thumbnail map', { size: thumbnailMap.size, linkedImages: linkedImagesResponse.images?.length });

          // Helper to merge thumbnail URLs into image assets
          const mergeUrls = (assets: RDAImageAsset[]): RDAImageAsset[] => {
            return assets.map((asset) => ({
              ...asset,
              url: thumbnailMap.get(asset.asset_id) || asset.url,
            }));
          };

          setMarketingImages(mergeUrls(rdaResponse.marketing_images || []));
          setSquareMarketingImages(mergeUrls(rdaResponse.square_marketing_images || []));
          setLogoImages(mergeUrls(rdaResponse.logo_images || []));
          setLandscapeLogoImages(mergeUrls(rdaResponse.landscape_logo_images || []));
          debug.log('Loaded RDA data with thumbnails', rdaResponse);
        } else {
          setError(rdaResponse.error || 'Failed to load RDA');
        }
      } catch (err) {
        console.error('Error loading RDA:', err);
        setError('Failed to load RDA data');
      } finally {
        setLoading(false);
      }
    }

    loadRDA();
  }, [memberUuid, adGroupId, adId, customerId, initialData]);

  // Update headline text
  const handleHeadlineChange = (index: number, text: string) => {
    if (text.length > HEADLINE_LIMIT) return;
    const updated = [...headlines];
    updated[index] = text;
    setHeadlines(updated);
  };

  // Add new headline
  const handleAddHeadline = () => {
    if (headlines.length >= MAX_HEADLINES) {
      toast.warning(`Maximum ${MAX_HEADLINES} headlines allowed`);
      return;
    }
    setHeadlines([...headlines, '']);
  };

  // Remove headline
  const handleRemoveHeadline = (index: number) => {
    if (headlines.length <= MIN_HEADLINES) {
      toast.warning(`Minimum ${MIN_HEADLINES} headline required`);
      return;
    }
    setHeadlines(headlines.filter((_, i) => i !== index));
  };

  // Update long headline
  const handleLongHeadlineChange = (text: string) => {
    if (text.length > LONG_HEADLINE_LIMIT) return;
    setLongHeadline(text);
  };

  // Update description text
  const handleDescriptionChange = (index: number, text: string) => {
    if (text.length > DESCRIPTION_LIMIT) return;
    const updated = [...descriptions];
    updated[index] = text;
    setDescriptions(updated);
  };

  // Add new description
  const handleAddDescription = () => {
    if (descriptions.length >= MAX_DESCRIPTIONS) {
      toast.warning(`Maximum ${MAX_DESCRIPTIONS} descriptions allowed`);
      return;
    }
    setDescriptions([...descriptions, '']);
  };

  // Remove description
  const handleRemoveDescription = (index: number) => {
    if (descriptions.length <= MIN_DESCRIPTIONS) {
      toast.warning(`Minimum ${MIN_DESCRIPTIONS} description required`);
      return;
    }
    setDescriptions(descriptions.filter((_, i) => i !== index));
  };

  // Update business name
  const handleBusinessNameChange = (text: string) => {
    if (text.length > BUSINESS_NAME_LIMIT) return;
    setBusinessName(text);
  };

  // Open image picker for specific image type
  const openImagePicker = (mode: ImagePickerMode) => {
    setImagePickerMode(mode);
    setShowImagePicker(true);
  };

  // Handle image picker confirmation — store pending crops in memory; do NOT
  // materialize Targeted MediaImages here. Materialization happens once at
  // Save Changes time (handleSave below).
  const handleImageConfirm = (pending: PendingTargeted[]) => {
    switch (imagePickerMode) {
      case 'marketing':
        setNewMarketingPending(pending);
        break;
      case 'square':
        setNewSquarePending(pending);
        break;
      case 'logo':
        setNewLogoPending(pending);
        break;
      case 'landscapeLogo':
        setNewLandscapeLogoPending(pending);
        break;
    }
    setShowImagePicker(false);
  };

  // Remove original image (by asset_id)
  const handleRemoveOriginalImage = (type: ImagePickerMode, assetId: string) => {
    switch (type) {
      case 'marketing':
        setMarketingImages(marketingImages.filter(img => img.asset_id !== assetId));
        break;
      case 'square':
        setSquareMarketingImages(squareMarketingImages.filter(img => img.asset_id !== assetId));
        break;
      case 'logo':
        setLogoImages(logoImages.filter(img => img.asset_id !== assetId));
        break;
      case 'landscapeLogo':
        setLandscapeLogoImages(landscapeLogoImages.filter(img => img.asset_id !== assetId));
        break;
    }
  };

  const handleRemoveNewImage = (type: ImagePickerMode, key: string) => {
    const filter = (prev: PendingTargeted[]) => prev.filter((p) => pendingKey(p) !== key);
    switch (type) {
      case 'marketing':
        setNewMarketingPending(filter);
        break;
      case 'square':
        setNewSquarePending(filter);
        break;
      case 'logo':
        setNewLogoPending(filter);
        break;
      case 'landscapeLogo':
        setNewLandscapeLogoPending(filter);
        break;
    }
  };

  // Map the local UI mode to the spec's placement_id. The TargetedImagePicker
  // owns the aspect-ratio resolution from there (via `usageContext.placementKey`)
  // so we no longer need to track aspect ratios or the spec registry here.
  const placementForMode: Record<ImagePickerMode, string> = {
    marketing: 'marketing_image',
    square: 'square_marketing_image',
    logo: 'logo',
    landscapeLogo: 'landscape_logo',
  };

  // Per-mode max-selection caps (Google's RDA limits). Aspect ratio is now
  // owned by the Targeted picker via `usageContext.placementKey`, so we no
  // longer need to compute it here.
  const MAX_SELECTION_FOR_MODE: Record<ImagePickerMode, number> = {
    marketing: 15,
    square: 15,
    logo: 5,
    landscapeLogo: 5,
  };
  const pickerConfig = { maxSelection: MAX_SELECTION_FOR_MODE[imagePickerMode] };

  // Handle landing page URL change
  const handleLandingPageChange = (url: string) => {
    setFinalUrls([url]);
  };

  // Validate and save
  const handleSave = async () => {
    // Validate headlines
    const validHeadlines = headlines.filter(h => h.trim());
    if (validHeadlines.length < MIN_HEADLINES) {
      toast.error(`At least ${MIN_HEADLINES} headline is required`);
      return;
    }

    // Validate long headline
    if (!longHeadline.trim()) {
      toast.error('Long headline is required');
      return;
    }

    // Validate descriptions
    const validDescriptions = descriptions.filter(d => d.trim());
    if (validDescriptions.length < MIN_DESCRIPTIONS) {
      toast.error(`At least ${MIN_DESCRIPTIONS} description is required`);
      return;
    }

    // Check for empty fields
    if (headlines.some(h => !h.trim())) {
      toast.error('Please fill in all headline fields or remove empty ones');
      return;
    }
    if (descriptions.some(d => !d.trim())) {
      toast.error('Please fill in all description fields or remove empty ones');
      return;
    }

    // Validate that we have at least 1 landscape and 1 square image
    const totalMarketing = marketingImages.length + newMarketingPending.length;
    const totalSquare = squareMarketingImages.length + newSquarePending.length;
    if (totalMarketing < 1) {
      toast.error('At least 1 landscape marketing image (1.91:1) is required');
      return;
    }
    if (totalSquare < 1) {
      toast.error('At least 1 square marketing image (1:1) is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Materialize the pending crops now (each call writes one Targeted
      // MediaImage to GCS + DB). Done in parallel within each placement
      // bucket and across buckets — typical RDA save uploads 4–10 images,
      // serializing would be O(n × ~3s).
      const [marketingMaterialized, squareMaterialized, logoMaterialized, landscapeLogoMaterialized] =
        await Promise.all([
          materializePending(newMarketingPending),
          materializePending(newSquarePending),
          materializePending(newLogoPending),
          materializePending(newLandscapeLogoPending),
        ]);

      const response = await updateRDA({
        member_uuid: memberUuid,
        campaign_id: campaignId,
        ad_group_id: adGroupId,
        ad_id: adId,
        customer_id: customerId,
        headlines,
        long_headline: longHeadline,
        descriptions,
        business_name: businessName,
        // Existing images (may have been modified by removing some)
        marketing_images: marketingImages.map(img => img.asset_id),
        square_marketing_images: squareMarketingImages.map(img => img.asset_id),
        logo_images: logoImages.map(img => img.asset_id),
        landscape_logo_images: landscapeLogoImages.map(img => img.asset_id),
        // Newly-materialized Targeted IDs from the just-confirmed crops.
        // Backend resolves these to their cropped GCS bytes (see
        // `_resolve_targeted_bytes_for_upload` — Targeted-id branch).
        new_marketing_image_ids: marketingMaterialized.length > 0 ? marketingMaterialized.map((t) => t.id) : undefined,
        new_square_image_ids: squareMaterialized.length > 0 ? squareMaterialized.map((t) => t.id) : undefined,
        new_logo_image_ids: logoMaterialized.length > 0 ? logoMaterialized.map((t) => t.id) : undefined,
        new_landscape_logo_image_ids: landscapeLogoMaterialized.length > 0 ? landscapeLogoMaterialized.map((t) => t.id) : undefined,
        // Final URLs
        final_urls: finalUrls.length > 0 ? finalUrls : undefined,
        trigger_data_sync: true,
      });

      if (response.success) {
        toast.success(response.message);
        onSaved();
      } else {
        setError(response.error || 'Failed to update RDA');
        toast.error(response.error || 'Failed to update RDA');
      }
    } catch (err) {
      console.error('Error updating RDA:', err);
      setError('Failed to update RDA');
      toast.error('Failed to update RDA');
    } finally {
      setSaving(false);
    }
  };

  // Count total images (original + new from Media Library)
  const totalImages = marketingImages.length + squareMarketingImages.length +
                      logoImages.length + landscapeLogoImages.length +
                      newMarketingPending.length + newSquarePending.length +
                      newLogoPending.length + newLandscapeLogoPending.length;

  // Error state with no data - show error with back button
  if (error && headlines.length === 0 && !loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow border max-w-4xl w-full">
        <div className="flex items-start justify-between mb-4 gap-2">
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-semibold text-gray-900">Manage Ad Assets</h2>
            <p className="text-sm text-gray-500">
              RDA in <span className="font-semibold text-gray-700">{adGroupName || 'Ad Group'}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="flex-shrink-0 p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            aria-label="Close editor"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="text-red-600 mb-4">{error}</div>
        <Button onClick={onCancel} variant="secondary">
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow border max-w-4xl w-full">
      <div className="flex items-start justify-between mb-4 gap-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-semibold text-gray-900">Manage Ad Assets</h2>
          <p className="text-sm text-gray-500">
            RDA in <span className="font-semibold text-gray-700">{adGroupName || 'Ad Group'}</span>
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="flex-shrink-0 p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          aria-label="Close editor"
        >
          <CloseIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {/* Error message (when we have data but encountered an error) */}
      {!loading && error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Content (only shown when not loading) */}
      {!loading && (
        <>
          {/* Landing Page URL */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Landing Page URL
            </label>
            <Input
              value={finalUrls[0] || ''}
              onChange={(e) => handleLandingPageChange(e.target.value)}
              placeholder="https://example.com/landing-page"
              className="flex-1"
            />
          </div>

          {/* Business Name */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Business Name
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Max {BUSINESS_NAME_LIMIT} characters
            </p>
            <div className="flex items-center gap-2">
              <Input
                value={businessName}
                onChange={(e) => handleBusinessNameChange(e.target.value)}
                placeholder="Your business name"
                maxLength={BUSINESS_NAME_LIMIT}
                className="flex-1"
              />
              <span className="text-xs text-gray-400 w-12">
                {businessName.length}/{BUSINESS_NAME_LIMIT}
              </span>
            </div>
          </div>

          {/* Headlines Section */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Short Headlines ({headlines.length}/{MAX_HEADLINES})
              </label>
              <Button
                onClick={handleAddHeadline}
                variant="ghost"
                size="sm"
                disabled={headlines.length >= MAX_HEADLINES}
                className="self-start sm:self-auto mt-1 sm:mt-0"
              >
                + Add Headline
              </Button>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              {MIN_HEADLINES}-{MAX_HEADLINES} headlines allowed, max {HEADLINE_LIMIT} characters each
            </p>
            <div className="space-y-3">
              {headlines.map((headline, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-3 sm:border-0 sm:p-0">
                  {/* Input row */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 w-6 flex-shrink-0">{index + 1}.</span>
                    <Input
                      value={headline}
                      onChange={(e) => handleHeadlineChange(index, e.target.value)}
                      placeholder={`Headline ${index + 1}`}
                      maxLength={HEADLINE_LIMIT}
                      className="flex-1"
                      aria-label={`Headline ${index + 1}`}
                    />
                    {/* Desktop: inline controls */}
                    <span className="text-xs text-gray-400 w-12 flex-shrink-0 hidden sm:block">
                      {headline.length}/{HEADLINE_LIMIT}
                    </span>
                    <Button
                      onClick={() => handleRemoveHeadline(index)}
                      variant="ghost"
                      size="sm"
                      disabled={headlines.length <= MIN_HEADLINES}
                      className="hidden sm:flex text-red-500 hover:text-red-700"
                      aria-label={`Remove headline ${index + 1}`}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </div>
                  {/* Mobile: controls on new line */}
                  <div className="flex items-center justify-between mt-2 pl-8 sm:hidden">
                    <span className="text-xs text-gray-400">
                      {headline.length}/{HEADLINE_LIMIT}
                    </span>
                    <Button
                      onClick={() => handleRemoveHeadline(index)}
                      variant="ghost"
                      size="sm"
                      disabled={headlines.length <= MIN_HEADLINES}
                      className="text-red-500 hover:text-red-700"
                      aria-label={`Remove headline ${index + 1}`}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Long Headline */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Long Headline
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Required, max {LONG_HEADLINE_LIMIT} characters
            </p>
            <div className="flex items-start gap-2">
              <textarea
                value={longHeadline}
                onChange={(e) => handleLongHeadlineChange(e.target.value)}
                placeholder="A longer headline for your ad"
                maxLength={LONG_HEADLINE_LIMIT}
                rows={2}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
              />
              <span className="text-xs text-gray-400 w-12 pt-2">
                {longHeadline.length}/{LONG_HEADLINE_LIMIT}
              </span>
            </div>
          </div>

          {/* Descriptions Section */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Descriptions ({descriptions.length}/{MAX_DESCRIPTIONS})
              </label>
              <Button
                onClick={handleAddDescription}
                variant="ghost"
                size="sm"
                disabled={descriptions.length >= MAX_DESCRIPTIONS}
                className="self-start sm:self-auto mt-1 sm:mt-0"
              >
                + Add Description
              </Button>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              {MIN_DESCRIPTIONS}-{MAX_DESCRIPTIONS} descriptions allowed, max {DESCRIPTION_LIMIT} characters each
            </p>
            <div className="space-y-3">
              {descriptions.map((description, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-3 sm:border-0 sm:p-0">
                  {/* Input row */}
                  <div className="flex items-start gap-2">
                    <span className="text-xs text-gray-400 w-6 pt-2 flex-shrink-0">{index + 1}.</span>
                    <textarea
                      value={description}
                      onChange={(e) => handleDescriptionChange(index, e.target.value)}
                      placeholder={`Description ${index + 1}`}
                      maxLength={DESCRIPTION_LIMIT}
                      rows={2}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                      aria-label={`Description ${index + 1}`}
                    />
                    {/* Desktop: inline controls */}
                    <span className="text-xs text-gray-400 w-12 pt-2 flex-shrink-0 hidden sm:block">
                      {description.length}/{DESCRIPTION_LIMIT}
                    </span>
                    <Button
                      onClick={() => handleRemoveDescription(index)}
                      variant="ghost"
                      size="sm"
                      disabled={descriptions.length <= MIN_DESCRIPTIONS}
                      className="hidden sm:flex text-red-500 hover:text-red-700 mt-1"
                      aria-label={`Remove description ${index + 1}`}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </div>
                  {/* Mobile: controls on new line */}
                  <div className="flex items-center justify-between mt-2 pl-8 sm:hidden">
                    <span className="text-xs text-gray-400">
                      {description.length}/{DESCRIPTION_LIMIT}
                    </span>
                    <Button
                      onClick={() => handleRemoveDescription(index)}
                      variant="ghost"
                      size="sm"
                      disabled={descriptions.length <= MIN_DESCRIPTIONS}
                      className="text-red-500 hover:text-red-700"
                      aria-label={`Remove description ${index + 1}`}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Images Section - Editable */}
          <div className="mb-6 space-y-4">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Images ({totalImages} total)
              </label>
            </div>
            <p className="text-xs text-gray-500 -mt-2">
              Click on images to remove. Use "Add" buttons to select new images from your Media Library.
            </p>

            {/* Landscape Marketing Images (1.91:1) - Required */}
            <div>
              <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Landscape Images <span className="hidden sm:inline">(1.91:1)</span> <span className="text-red-500">*</span>
                </label>
                <span className="text-xs text-gray-500">
                  {marketingImages.length + newMarketingPending.length}/15 (min 1 required)
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mb-2">
                {/* Original images from Google Ads */}
                {marketingImages.map((img) => (
                  <ImagePreview
                    key={`orig-marketing-${img.asset_id}`}
                    imageUrl={img.url || undefined}
                    assetId={img.asset_id}
                    aspectRatio="1.91:1"
                    onRemove={() => handleRemoveOriginalImage('marketing', img.asset_id)}
                    label="Marketing Image"
                  />
                ))}
                {/* New (pending) crops — preview from in-memory PendingTargeted */}
                {newMarketingPending.map((p) => {
                  const k = pendingKey(p);
                  return (
                    <PendingPreview
                      key={`new-marketing-${k}`}
                      pending={p}
                      aspectRatio="1.91:1"
                      onRemove={() => handleRemoveNewImage('marketing', k)}
                    />
                  );
                })}
                {/* Add button */}
                <button
                  type="button"
                  onClick={() => openImagePicker('marketing')}
                  className="w-20 h-10 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
                >
                  <PhotoIcon className="w-5 h-5" />
                </button>
              </div>
              <p className="text-xs text-gray-500">Recommended: 1200x628px, max 5MB</p>
            </div>

            {/* Square Marketing Images (1:1) - Required */}
            <div>
              <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Square Images <span className="hidden sm:inline">(1:1)</span> <span className="text-red-500">*</span>
                </label>
                <span className="text-xs text-gray-500">
                  {squareMarketingImages.length + newSquarePending.length}/15 (min 1 required)
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mb-2">
                {/* Original images from Google Ads */}
                {squareMarketingImages.map((img) => (
                  <ImagePreview
                    key={`orig-square-${img.asset_id}`}
                    imageUrl={img.url || undefined}
                    assetId={img.asset_id}
                    aspectRatio="1:1"
                    onRemove={() => handleRemoveOriginalImage('square', img.asset_id)}
                    label="Square Image"
                  />
                ))}
                {/* New (pending) crops */}
                {newSquarePending.map((p) => {
                  const k = pendingKey(p);
                  return (
                    <PendingPreview
                      key={`new-square-${k}`}
                      pending={p}
                      aspectRatio="1:1"
                      onRemove={() => handleRemoveNewImage('square', k)}
                    />
                  );
                })}
                {/* Add button */}
                <button
                  type="button"
                  onClick={() => openImagePicker('square')}
                  className="w-16 h-16 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
                >
                  <PhotoIcon className="w-5 h-5" />
                </button>
              </div>
              <p className="text-xs text-gray-500">Recommended: 1200x1200px, max 5MB</p>
            </div>

            {/* Logos - Optional */}
            <div className="grid grid-cols-2 gap-4">
              {/* Square Logo (1:1) */}
              <div>
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Square Logo <span className="hidden sm:inline">(1:1)</span>
                  </label>
                  <span className="text-xs text-gray-500">
                    {logoImages.length + newLogoPending.length}/5
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {/* Original logos */}
                  {logoImages.map((img) => (
                    <ImagePreview
                      key={`orig-logo-${img.asset_id}`}
                      imageUrl={img.url || undefined}
                      assetId={img.asset_id}
                      aspectRatio="1:1"
                      onRemove={() => handleRemoveOriginalImage('logo', img.asset_id)}
                      label="Logo"
                    />
                  ))}
                  {/* New logos from Media Library */}
                  {newLogoPending.map((p) => {
                    const k = pendingKey(p);
                    return (
                      <PendingPreview
                        key={`new-logo-${k}`}
                        pending={p}
                        aspectRatio="1:1"
                        onRemove={() => handleRemoveNewImage('logo', k)}
                      />
                    );
                  })}
                  {/* Add button */}
                  <button
                    type="button"
                    onClick={() => openImagePicker('logo')}
                    className="w-12 h-12 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
                  >
                    <PhotoIcon className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-gray-500">Optional, 1200x1200px recommended</p>
              </div>

              {/* Landscape Logo (4:1) */}
              <div>
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Landscape Logo <span className="hidden sm:inline">(4:1)</span>
                  </label>
                  <span className="text-xs text-gray-500">
                    {landscapeLogoImages.length + newLandscapeLogoPending.length}/5
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {/* Original landscape logos */}
                  {landscapeLogoImages.map((img) => (
                    <ImagePreview
                      key={`orig-llogo-${img.asset_id}`}
                      imageUrl={img.url || undefined}
                      assetId={img.asset_id}
                      aspectRatio="4:1"
                      onRemove={() => handleRemoveOriginalImage('landscapeLogo', img.asset_id)}
                      label="Landscape Logo"
                    />
                  ))}
                  {/* New (pending) landscape logos */}
                  {newLandscapeLogoPending.map((p) => (
                    <PendingPreview
                      key={`new-llogo-${pendingKey(p)}`}
                      pending={p}
                      aspectRatio="4:1"
                      onRemove={() => handleRemoveNewImage('landscapeLogo', pendingKey(p))}
                    />
                  ))}
                  {/* Add button */}
                  <button
                    type="button"
                    onClick={() => openImagePicker('landscapeLogo')}
                    className="w-20 h-5 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
                  >
                    <PhotoIcon className="w-3 h-3" />
                  </button>
                </div>
                <p className="text-xs text-gray-500">Optional, 1200x300px recommended</p>
              </div>
            </div>
          </div>

          {/* Image Picker Modal — TargetedImagePicker returns PendingTargeted[]
              (in-memory). No GCS write here; materialization happens once at
              Save Changes click in handleSave below. */}
          <TargetedImagePicker
            isOpen={showImagePicker}
            onClose={() => setShowImagePicker(false)}
            usageContext={{
              platform: 'google_ads',
              placementKey: `google_ads.${placementForMode[imagePickerMode]}`,
            }}
            onConfirm={handleImageConfirm}
            maxSelection={pickerConfig.maxSelection}
          />

          {/* Save/Cancel Buttons */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button onClick={onCancel} variant="secondary" disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} variant="primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default RDAEditor;
